import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SesamLink from 'Common/Links/SesamLink';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';

import { PipeActions } from 'Redux/thunks/pipes.thunks';
import { useFlowEntities } from '../../hooks/useFlowEntities.hook';
import { useFlowTabs } from '../../hooks/useFlowTabs.hook';
import { getFlow } from '../../utils/flows.utils';

import ErrorBoundary from 'Common/ErrorBoundary/ErrorBoundary';
import { LoadingPanel } from 'Common/LoadingPanel';
import Page from '../../components/page';
import PageHeader, { PageHeaderTitle } from '../../components/page-header';
import { TabbedNav } from '../../components/tabbed-nav/TabbedNav.view';

import type { RootState } from '../../types/state.types';
import type { FlowInputOutput } from '../../types/flows.types';

/**
 * Takes the path from the url and turns it into a Flow (an array of pipes),
 * then passes it down to its children.
 */
export const FlowPage: React.FC<{
  children: React.ReactNode;
  location: { query: { path?: string | string[] } };
}> = ({ children, location }) => {
  const dispatch = useDispatch();

  const [flow, setFlow] = useState<FlowInputOutput[] | null | undefined>(undefined);

  const pipes = useSelector((state: RootState) => state.pipes);
  const systems = useSelector((state: RootState) => state.systems);
  const subId = useSelector((state: RootState) => state.subscription.id);
  const subUrl = useSelector((state: RootState) => state.subscription.url);
  const token = useSelector((state: RootState) => state.subscription.token);

  const previewPipe = useCallback(
    (pipe: any, entity: any) => dispatch(PipeActions.previewPipe(pipe, entity) as any),
    [dispatch]
  );

  const entities = useFlowEntities(flow, subUrl, token, previewPipe);
  const pathFromUrl = get(location, 'query.path') as string | string[] | undefined;
  const nav = useFlowTabs(subId, pathFromUrl);

  useEffect(() => {
    if (isEmpty(pipes) || isEmpty(systems)) {
      return;
    }

    setFlow(getFlow(pathFromUrl, pipes, systems));
  }, [pipes, systems, pathFromUrl]);

  if (!flow) {
    return (
      <ErrorBoundary>
        <Page className="page--flow">
          <LoadingPanel loadingMessage="Loading flow..." />
        </Page>
      </ErrorBoundary>
    );
  }

  const errorMsg =
    !flow &&
    "Error: Either you have not supplied a path in the url, or one of the id's does not belong to a pipe";

  const childrenWithProps = React.Children.map(children, (child) =>
    React.cloneElement(child as React.ReactElement<any>, { flow, subId, entities })
  );

  return (
    <ErrorBoundary>
      <Page className="page--flow">
        <PageHeader style={undefined}>
          <PageHeaderTitle>
            <SesamLink to={`/subscription/${subId}/flows`}>Flows</SesamLink>
          </PageHeaderTitle>
          {flow && flow.length > 0 && (
            <PageHeaderTitle>{`${get(flow[0], 'data._id')} -> ${get(flow[flow.length - 1], 'data._id')}`}</PageHeaderTitle>
          )}
        </PageHeader>
        <TabbedNav nav={nav}>{flow ? childrenWithProps : errorMsg}</TabbedNav>
      </Page>
    </ErrorBoundary>
  );
};
